import type { Frame } from '../store/types';
import { dispatch } from '../store/canvas-store';

/**
 * Wire inline contenteditable slot editing on a frame-content DOM root.
 *
 * For instance frames only: walk `data-slot="name"` elements, attach click
 * handler that toggles contenteditable="plaintext-only". On blur or Escape,
 * read innerText and dispatch instance.setSlot. Click outside / blur commits.
 *
 * Returns cleanup fn that removes handlers.
 */
export function wireInlineSlotEdit(root: HTMLElement, frame: Frame): () => void {
  if (frame.kind !== 'instance') return () => {};

  const cleanups: Array<() => void> = [];
  const slotEls = root.querySelectorAll('[data-slot]');

  for (const el of Array.from(slotEls)) {
    const slotName = el.getAttribute('data-slot');
    if (!slotName) continue;
    const html = el as HTMLElement;

    // Visual cue: dotted amber underline on hover
    html.style.cursor = 'text';
    html.style.outline = 'none';
    const onMouseEnter = () => {
      html.style.boxShadow = 'inset 0 -1px 0 var(--color-tool-code)';
    };
    const onMouseLeave = () => {
      if (html.contentEditable !== 'true') html.style.boxShadow = '';
    };
    const onClick = (e: MouseEvent) => {
      e.stopPropagation();
      html.contentEditable = 'plaintext-only';
      html.focus();
      // Select all so user can replace text immediately
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(html);
      sel?.removeAllRanges();
      sel?.addRange(range);
    };
    const commit = () => {
      if (html.contentEditable !== 'true') return;
      const value = html.innerText;
      html.contentEditable = 'false';
      html.style.boxShadow = '';
      dispatch({ t: 'instance.setSlot', id: frame.id, slotName, value });
    };
    const onBlur = () => commit();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        html.contentEditable = 'false';
        html.style.boxShadow = '';
        e.stopPropagation();
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        commit();
        html.blur();
      }
    };

    html.addEventListener('mouseenter', onMouseEnter);
    html.addEventListener('mouseleave', onMouseLeave);
    html.addEventListener('click', onClick);
    html.addEventListener('blur', onBlur);
    html.addEventListener('keydown', onKeyDown);
    cleanups.push(() => {
      html.removeEventListener('mouseenter', onMouseEnter);
      html.removeEventListener('mouseleave', onMouseLeave);
      html.removeEventListener('click', onClick);
      html.removeEventListener('blur', onBlur);
      html.removeEventListener('keydown', onKeyDown);
    });
  }

  return () => {
    for (const c of cleanups) c();
  };
}
